import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved,
  faLock,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import VocUploader from "./VocUploader";
import { CATEGORY_ICON_COMPONENTS } from "./VocIcons";
import {
  CATEGORIES,
  CONTACT_STATUSES,
  EDUCATION_LEVELS,
  YEARS_OF_STUDY,
  STAFF_TYPES,
  localizeOptions,
  parseOptionPairs,
} from "../../services/voc";

function Field({ label, required, error, children, hint }) {
  return (
    <div>
      <label className="block text-meta font-bold text-slateGray text-left mb-[8px]">
        {label}
        {required && <span className="text-brand">*</span>}
      </label>
      {children}
      {hint && <p className="text-small text-metaGray mt-[6px] text-left">{hint}</p>}
      {error && (
        <p className="flex items-center gap-[6px] text-small text-brand mt-[6px] text-left">
          <FontAwesomeIcon icon={faCircleExclamation} />
          {error}
        </p>
      )}
    </div>
  );
}

const controlClass = (hasError) =>
  `w-full rounded-[6px] border-[1px] bg-white px-[14px] py-[10px] text-body text-slateGray outline-none transition-colors placeholder:text-metaGray ` +
  (hasError
    ? "border-brand focus:border-brand"
    : "border-cardBorder hover:border-metaGray focus:border-slateGray");

/** ดรอปดาวน์ที่เว้นว่างไว้ได้ ถ้าไม่มีตัวเลือกจะกลายเป็นช่องพิมพ์เองแทน */
function Choice({ value, onChange, options, placeholder, hasError }) {
  if (options.length === 0) {
    return (
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={controlClass(hasError)}
      />
    );
  }

  return (
    <select value={value} onChange={onChange} className={controlClass(hasError)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * ฟอร์มขั้นตอนที่ 2 — ทุกช่องและทุกสถานะอ้างจากไฟล์ design
 *
 * ช่องย่อยของนักศึกษา/บุคลากรจะโผล่ตามสถานภาพที่เลือก
 * รายชื่อคณะกับหน่วยงานดึงจาก VOC Setting เพราะเป็นข้อมูลจริงของมหาวิทยาลัย
 */
export default function VocForm({
  value,
  onChange,
  files,
  onFilesChange,
  errors,
  lang,
  setting,
  onSubmit,
}) {
  const t = (th, en) => (lang === "th" ? th : en);
  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });
  const pleaseSelect = `---${t("โปรดระบุ", "Please select")}---`;

  // ค่าที่บันทึกเป็นภาษาไทยเสมอ ที่เปลี่ยนตามภาษาคือข้อความที่โชว์เท่านั้น
  const facultyOptions = parseOptionPairs(
    setting?.facultyOptions,
    setting?.facultyOptions_EN,
    lang
  );
  const departmentOptions = parseOptionPairs(
    setting?.departmentOptions,
    setting?.departmentOptions_EN,
    lang
  );
  // ประเภทบุคลากรตั้งใน VOC Setting ได้ ถ้ายังไม่ได้กรอกใช้ชุดเดิมในโค้ด (ไม่เป็นช่องพิมพ์เอง)
  const customStaffTypes = parseOptionPairs(
    setting?.staffTypeOptions,
    setting?.staffTypeOptions_EN,
    lang
  );
  const staffTypeOptions =
    customStaffTypes.length > 0 ? customStaffTypes : localizeOptions(STAFF_TYPES, lang);

  return (
    <form
      className="rounded-[12px] bg-lightGray3/60 p-[16px] sm:p-[24px] xl:p-[40px] mt-[24px]"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-col gap-[24px]">
        <Field
          label={t("เลือกประเภทความคิดเห็น", "Type of feedback")}
          required
          error={errors.category}
        >
          <div className="grid grid-cols-4 gap-[8px] sm:gap-[16px]">
            {CATEGORIES.map((c) => {
              const CategoryIcon = CATEGORY_ICON_COMPONENTS[c.value];
              const active = value.category === c.value;

              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => onChange({ ...value, category: c.value })}
                  className={
                    `rounded-[6px] border-[1px] px-[4px] sm:px-[10px] py-[12px] sm:py-[18px] flex flex-col items-center gap-[6px] sm:gap-[10px] transition-colors ` +
                    // ตาม design: ไม่มีสถานะสีเทาเลย ทั้งตอนชี้และตอนเลือก
                    // จะเป็นโทนส้ม ต่างกันแค่ความเข้มของพื้นหลัง
                    (active
                      ? "border-brand bg-[#FFF3EF] text-brand"
                      : "border-cardBorder bg-white text-slateGray hover:border-brand hover:text-brand hover:bg-[#FFFAF8]")
                  }
                >
                  <CategoryIcon className="w-[20px] h-[20px] sm:w-[24px] sm:h-[24px]" />
                  <span className="text-small sm:text-meta">
                    {lang === "th" ? c.label_th : c.label_en}
                  </span>
                </button>
              );
            })}
          </div>
        </Field>

        <Field
          label={t(
            "ชื่อ-นามสกุล หรือชื่อที่ท่านสะดวกให้เราเรียก",
            "Name, or what we should call you"
          )}
          required
          error={errors.fullName}
        >
          <input
            type="text"
            value={value.fullName}
            onChange={set("fullName")}
            placeholder={t("ระบุ ชื่อ-นามสกุล", "Enter your name")}
            className={controlClass(errors.fullName)}
          />
        </Field>

        <Field
          label="Email"
          error={errors.email}
          hint={t(
            "ใช้สำหรับติดต่อสอบถามข้อมูลเพิ่มเติม และแจ้งความคืบหน้าในการดำเนินการ หากไม่ระบุ เจ้าหน้าที่จะไม่สามารถแจ้งผลกลับทางอีเมลได้",
            "Used to follow up and to tell you how your case is progressing. Without it we cannot reply by email."
          )}
        >
          <input
            type="email"
            value={value.email}
            onChange={set("email")}
            placeholder={t("ระบุ Email", "Enter your email")}
            className={controlClass(errors.email)}
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-[16px]">
          <Field
            label={t("สถานภาพปัจจุบันของท่าน", "You are")}
            required
            error={errors.contactStatus}
          >
            <div className="max-w-[240px] sm:max-w-none">
            <select
              value={value.contactStatus}
              onChange={(e) =>
                onChange({
                  ...value,
                  contactStatus: e.target.value,
                  // ล้างช่องเฉพาะกลุ่ม กันข้อมูลค้างจากตัวเลือกก่อนหน้า
                  faculty: "",
                  educationLevel: "",
                  yearOfStudy: "",
                  staffType: "",
                })
              }
              className={controlClass(errors.contactStatus)}
            >
              <option value="">{pleaseSelect}</option>
              {CONTACT_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {lang === "th" ? s.label_th : s.label_en}
                </option>
              ))}
            </select>
            </div>
          </Field>

          {/* ช่องเพิ่มเติมของนักศึกษา */}
          {value.contactStatus === "student" && (
            <>
              <Field label={t("คณะที่ท่านกำลังศึกษาอยู่", "Faculty")}>
                <Choice
                  value={value.faculty}
                  onChange={set("faculty")}
                  options={facultyOptions}
                  placeholder={pleaseSelect}
                />
              </Field>
              <Field label={t("ระดับการศึกษา", "Level of study")}>
                <Choice
                  value={value.educationLevel}
                  onChange={set("educationLevel")}
                  options={localizeOptions(EDUCATION_LEVELS, lang)}
                  placeholder={pleaseSelect}
                />
              </Field>
              <div className="sm:col-start-2">
                <Field label={t("ชั้นปี", "Year")}>
                  <Choice
                    value={value.yearOfStudy}
                    onChange={set("yearOfStudy")}
                    options={localizeOptions(YEARS_OF_STUDY, lang)}
                    placeholder={pleaseSelect}
                  />
                </Field>
              </div>
            </>
          )}

          {/* ช่องเพิ่มเติมของบุคลากร */}
          {value.contactStatus === "staff" && (
            <>
              <Field
                label={t(
                  "หน่วยงาน/คณะ/ส่วนงาน ที่ท่านสังกัด",
                  "Department you belong to"
                )}
              >
                <Choice
                  value={value.faculty}
                  onChange={set("faculty")}
                  options={departmentOptions}
                  placeholder={pleaseSelect}
                />
              </Field>
              <Field label={t("ประเภทบุคลากร", "Staff type")}>
                <Choice
                  value={value.staffType}
                  onChange={set("staffType")}
                  options={staffTypeOptions}
                  placeholder={pleaseSelect}
                />
              </Field>
            </>
          )}
        </div>

        <Field label={t("รายละเอียด", "Details")} required error={errors.detail}>
          <textarea
            rows={5}
            value={value.detail}
            onChange={set("detail")}
            placeholder={t("ระบุ รายละเอียด", "Enter the details")}
            className={controlClass(errors.detail) + " resize-y"}
          />
        </Field>

        <Field
          label={t("รูปภาพประกอบ (สูงสุด 3 รูป)", "Images (up to 3)")}
          error={errors.attachments}
        >
          <VocUploader files={files} onChange={onFilesChange} lang={lang} />
        </Field>

        <div className="rounded-[6px] border-[1px] border-cardBorder bg-white p-[18px]">
          <p className="flex items-center gap-[8px] text-meta font-bold text-blue text-left mb-[12px]">
            <FontAwesomeIcon icon={faShieldHalved} />
            {t("การเผยแพร่ข้อมูล", "Publishing your feedback")}
          </p>

          <label className="flex items-start gap-[10px] cursor-pointer text-left">
            <input
              type="checkbox"
              checked={value.publicConsent}
              onChange={(e) =>
                onChange({ ...value, publicConsent: e.target.checked })
              }
              className="mt-[5px] w-[18px] h-[18px] accent-[#1CB5B1] shrink-0"
            />
            <span>
              <span className="block text-meta text-slateGray">
                {t(
                  "ยินยอมให้เผยแพร่ความคิดเห็นของท่าน โดยปกปิดข้อมูลส่วนบุคคล",
                  "I allow my feedback to be published with my personal details hidden"
                )}
              </span>
              <span className="block text-small text-metaGray mt-[2px]">
                {t(
                  "ข้อมูลที่เผยแพร่จะไม่รวมชื่อ อีเมล หรือข้อมูลอื่นที่สามารถระบุตัวตนได้",
                  "Published text never includes your name, email or anything identifying"
                )}
              </span>
            </span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[16px]">
          <p className="flex items-center gap-[10px] text-meta text-slateGray text-left">
            <FontAwesomeIcon icon={faLock} className="text-metaGray" />
            {t(
              "ข้อมูลของท่านจะถูกเก็บเป็นความลับ และใช้เพื่อการพัฒนาการบริการเท่านั้น",
              "Your information stays confidential and is used only to improve our service"
            )}
          </p>

          <button
            type="submit"
            className="self-end sm:self-auto shrink-0 rounded-[6px] bg-brand text-white text-body font-bold px-[36px] py-[12px] hover:opacity-90 transition-opacity"
          >
            {t("บันทึกข้อมูล", "Submit")}
          </button>
        </div>
      </div>
    </form>
  );
}
